import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  CheckCircle2, Shield, Award, Star, MapPin, Users, Hammer, Clock, Quote, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import craftsmanshipImage from '@/assets/deckpro (5).jpeg';
import teamImage from '@/assets/deckpro (7).jpeg';

/* ─── Hero ─────────────────────────────────────────────── */
const PageHero = () => (
  <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center max-w-4xl mx-auto">
        <p className="section-eyebrow justify-center">Our Company · Since 1999</p>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
          data-speakable
        >
          About <span className="text-lime">DeckPros NJ</span>
        </h1>
        <p
          className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body"
          data-speakable
        >
          A family-owned deck construction company building beautiful, durable outdoor spaces for New Jersey homeowners for over 25 years.
        </p>
      </div>
    </div>
  </section>
);

/* ─── Our Story ────────────────────────────────────────── */
const OurStory = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <section id="story" className="section-compact bg-cream">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-7xl mx-auto">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="overflow-hidden border-4 border-navy shadow-hard">
              <img
                src={craftsmanshipImage}
                alt="DeckPros NJ founders working on a custom deck in Morris County, New Jersey"
                className="w-full h-[380px] lg:h-[460px] object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-lime border-4 border-navy p-5 shadow-hard">
              <div className="font-display text-4xl font-bold text-navy leading-none">25+</div>
              <div className="font-display text-[10px] uppercase tracking-[0.2em] text-navy mt-1 font-bold">Years of NJ Craft</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <p className="section-eyebrow">Who We Are</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-4 leading-tight">
              American Craft. <br/><span className="text-lime">NJ Pride.</span>
            </h2>
            <p className="text-stone text-base leading-relaxed mb-4 font-body" data-speakable>
              DeckPros NJ was founded in 1999 in New Jersey with a simple mission: build outdoor spaces that last generations, using honest labor and premium American materials. What started as a one-truck family business has grown into one of New Jersey's most trusted deck contractors.
            </p>
            <p className="text-stone text-base leading-relaxed mb-6 font-body">
              Today, we've completed over 1,000 projects across Morris, Sussex, Warren, Hunterdon, and Somerset Counties — every single one built in-house by our certified crew. No subcontractors. No shortcuts. Just straight-up craftsmanship, on time and on budget.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-7">
              {[
                { icon: Users, label: 'Family-Owned & Operated' },
                { icon: Hammer, label: 'In-House Crew Only' },
                { icon: Shield, label: 'Licensed · Bonded · Insured' },
                { icon: Award, label: 'Lifetime Workmanship Warranty' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-2">
                  <item.icon className="w-5 h-5 text-lime flex-shrink-0 mt-0.5" />
                  <span className="text-navy text-sm font-body font-medium">{item.label}</span>
                </div>
              ))}
            </div>

            <Link to="/contact">
              <Button variant="cta" size="lg">
                Get a Free Estimate
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─── Core Values ─────────────────────────────────────── */
const CoreValues = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const values = [
    { n: '01', title: 'Excellence', desc: 'We never compromise on quality. Every board, every cut, every nail meets our standards.' },
    { n: '02', title: 'Integrity', desc: 'Honest pricing, clear communication, and standing behind every deck we build.' },
    { n: '03', title: 'Customer Focus', desc: 'Your vision drives the project. We listen, advise, and deliver what you asked for.' },
    { n: '04', title: 'Craftsmanship', desc: 'Skilled in-house professionals using proven techniques and premium American materials.' },
    { n: '05', title: 'Precision', desc: 'Attention to detail in every measurement, cut, fastener, and finish.' },
    { n: '06', title: 'Passion', desc: 'We love building outdoor living spaces — and it shows in every project we complete.' },
  ];

  return (
    <section id="values" className="section-compact bg-cream-dark">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-10 max-w-2xl mx-auto"
        >
          <p className="section-eyebrow justify-center">What Drives Us</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
            Our Core <span className="text-lime">Values</span>
          </h2>
          <p className="text-stone font-body">
            The six principles that shape every project — and every team member — at DeckPros NJ.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.06 }}
              className="group bg-white border-2 border-navy/10 hover:border-lime hover:-translate-y-1 hover:shadow-hard transition-all duration-200 p-6 relative overflow-hidden"
            >
              <span className="absolute top-2 right-3 font-display text-5xl font-bold text-navy/[0.06] leading-none select-none">
                {v.n}
              </span>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-0.5 bg-lime" />
                <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-lime">
                  {v.n}
                </span>
              </div>
              <h3 className="font-display text-lg font-bold text-navy uppercase tracking-wide mb-2 group-hover:text-lime transition-colors">
                {v.title}
              </h3>
              <p className="text-stone text-sm leading-relaxed font-body">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Why DeckPros / Trust ───────────────────────────── */
const WhyDeckPros = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <section className="section-compact bg-cream">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-7xl mx-auto">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="section-eyebrow">Why Homeowners Choose Us</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-4 leading-tight">
              Built Different. <br/><span className="text-lime">Built Better.</span>
            </h2>
            <p className="text-stone text-base leading-relaxed mb-6 font-body" data-speakable>
              We're not a franchise, not a lead-flip operation, and we don't chase the cheapest price. We're a New Jersey family that has been building decks the right way for 25+ years — and every job is a personal reputation bet.
            </p>

            <div className="space-y-3 mb-7">
              {[
                'Free on-site consultation & detailed written quote',
                'In-house team — no subcontractors, ever',
                '3D design renderings on request',
                'All permits, inspections & HOA paperwork handled',
                'Lifetime workmanship warranty',
                'Financing available (0% APR up to 12 months)',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-lime flex-shrink-0 mt-0.5" />
                  <span className="text-navy text-sm font-body font-medium">{item}</span>
                </div>
              ))}
            </div>

            <Link to="/services">
              <Button variant="default" size="lg">
                See Our Services
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative order-first lg:order-last"
          >
            <div className="overflow-hidden border-4 border-navy shadow-hard">
              <img
                src={teamImage}
                alt="DeckPros NJ in-house crew installing a premium composite deck"
                className="w-full h-[380px] lg:h-[460px] object-cover"
              />
            </div>
            <div className="absolute -top-4 -left-4 bg-navy text-white border-4 border-lime p-5 shadow-hard max-w-[200px]">
              <Quote className="w-5 h-5 text-lime mb-2" />
              <p className="text-sm italic leading-snug font-body">
                "No shortcuts. No subs. Just our crew and your deck."
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-lime mt-2 font-display font-bold">
                — Owner
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─── Certifications / Trust Seal ───────────────────── */
const Certifications = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const certs = [
    { title: 'Fully Licensed', desc: 'NJ Home Improvement Contractor #13VH09876500' },
    { title: 'Fully Bonded', desc: 'Surety bonded for client protection on every project' },
    { title: 'Fully Insured', desc: 'General liability + workers compensation coverage' },
    { title: 'A+ BBB Rated', desc: 'Accredited with the Better Business Bureau since 2010' },
  ];

  return (
    <section id="certifications" className="section-compact bg-navy text-white">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-10 max-w-2xl mx-auto"
        >
          <p className="section-eyebrow justify-center">Trust & Compliance</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold">
            Licensed, Bonded & <span className="text-lime">Insured</span>
          </h2>
          <p className="text-white/70 mt-3 font-body">
            Every project is backed by full licensing, bonding, and insurance — protecting your home and your wallet from start to finish.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto"
        >
          {certs.map(({ title, desc }) => (
            <div
              key={title}
              className="bg-navy-deep border-2 border-white/10 hover:border-lime transition-colors p-5 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-3 bg-lime flex items-center justify-center border-2 border-navy">
                <CheckCircle2 className="w-5 h-5 text-navy" />
              </div>
              <div className="font-display font-bold text-white text-sm uppercase tracking-wide mb-2">{title}</div>
              <div className="text-white/60 text-xs font-body leading-relaxed">{desc}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ─── FAQ (AEO / GEO) ───────────────────────────────── */
const aboutFaqs = [
  {
    question: 'Who is DeckPros NJ?',
    answer: 'DeckPros NJ is a family-owned deck construction and outdoor living contractor founded in 1999 in New Jersey. We design, build, and repair residential and commercial decks across Morris, Sussex, Warren, Hunterdon, and Somerset Counties.',
  },
  {
    question: 'How long has DeckPros NJ been in business?',
    answer: 'DeckPros NJ has been building decks in New Jersey for over 25 years. We were founded in 1999 and have completed more than 1,000 projects across northern and central NJ.',
  },
  {
    question: 'Is DeckPros NJ licensed and insured?',
    answer: 'Yes. DeckPros NJ holds an active NJ Home Improvement Contractor license, is fully bonded, and carries both general liability and workers compensation insurance. We are also A+ rated and accredited with the Better Business Bureau.',
  },
  {
    question: 'Does DeckPros NJ use subcontractors?',
    answer: 'No. Every DeckPros NJ project is built by our certified in-house crew from start to finish. We never use subcontractors, which means consistent quality, clear accountability, and a single point of contact on your project.',
  },
  {
    question: 'What areas of New Jersey does DeckPros NJ serve?',
    answer: 'DeckPros NJ serves Morris County, Sussex County, Warren County, Hunterdon County, and Somerset County — plus surrounding towns across northern and central New Jersey. Call (201) 657-0855 to confirm service in your town.',
  },
  {
    question: 'What warranty does DeckPros NJ provide?',
    answer: 'DeckPros NJ provides a lifetime workmanship warranty on every deck we build. Materials are covered under their manufacturer warranties — typically 25–50 years on composite/PVC products like Trex and TimberTech, and 10–25 years on premium wood.',
  },
];

const FAQSection = () => (
  <section className="section-compact bg-cream-dark">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">About DeckPros NJ</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          Common <span className="text-lime">Questions</span>
        </h2>
      </div>

      <div
        className="max-w-4xl mx-auto space-y-3"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {aboutFaqs.map((faq, i) => (
          <details
            key={i}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
          >
            <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
              <h3
                itemProp="name"
                className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
              >
                {faq.question}
              </h3>
              <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
            </summary>
            <div
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
              className="px-5 pb-5 -mt-1"
            >
              <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/* ─── AboutPage Schema (Organization + AboutPage) ───── */
const AboutPageSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About DeckPros NJ',
    description: 'Family-owned New Jersey deck builder since 1999. 25+ years of experience, 1,000+ projects, A+ BBB rated.',
    url: 'https://deckprosnj.com/about',
    mainEntity: {
      '@type': 'LocalBusiness',
      '@id': 'https://deckprosnj.com/#organization',
      name: 'DeckPros NJ',
      foundingDate: '1999',
      telephone: '+1-201-657-0855',
      address: { '@type': 'PostalAddress', addressRegion: 'NJ', addressCountry: 'US' },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
      award: ['A+ Better Business Bureau Accreditation', 'Trex Certified Installer', 'TimberTech Certified Installer'],
      numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 10, maxValue: 50 },
      knowsAbout: [
        'Deck Construction',
        'Composite Decking',
        'PVC Decking',
        'Wood Decking',
        'Deck Repair',
        'Outdoor Living',
        'Pergola Construction',
        'Railing Installation',
      ],
    },
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── Page ────────────────────────────────────────── */
const About = () => {
  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="About DeckPros NJ"
        description="Learn about DeckPros NJ — a family-owned New Jersey deck builder with 25+ years of experience, 1,000+ completed projects, and an A+ BBB rating. Licensed, bonded, and insured."
        canonical="/about"
        keywords="about DeckPros NJ, New Jersey deck builder, family-owned deck contractor NJ, licensed deck contractor, NJ deck company history, Morris County deck builder, trusted deck contractor"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'About', url: '/about' },
      ]} />
      <SpeakableSchema />
      <FaqSchema faqs={aboutFaqs} />
      <AboutPageSchema />

      <Header />
      <main>
        <PageHero />
        <OurStory />
        <WhyDeckPros />
        <CoreValues />
        <Certifications />
        <FAQSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default About;
